
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Clock, Bell, CheckCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MedicationAdherenceProps {
  stats: {
    takenPercentage: number;
    onTimePercentage: number;
    correctDosagePercentage: number;
  };
}

export const MedicationAdherence = ({ stats }: MedicationAdherenceProps) => {
  const { data: reminderCount } = useQuery({
    queryKey: ["medication-reminder-count"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from("medications")
        .select("*", { count: 'exact', head: true })
        .eq("user_id", user.id)
        .eq("active", true)
        .not("reminder_time", "is", null);

      if (error) throw error;
      return count || 0;
    },
  });

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-blue-600" />
          Suivi de prise de médicaments
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-muted-foreground">Prise effective</span>
              <span className="font-medium">{stats.takenPercentage}%</span>
            </div>
            <Progress value={stats.takenPercentage} className="h-2" />
          </div>

          <div>
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-muted-foreground">Ponctualité</span>
              <span className="font-medium">{stats.onTimePercentage}%</span>
            </div>
            <Progress value={stats.onTimePercentage} className="h-2" />
          </div>

          <div>
            <div className="flex justify-between items-center text-sm mb-1">
              <span className="text-muted-foreground">Dosage correct</span>
              <span className="font-medium">{stats.correctDosagePercentage}%</span>
            </div>
            <Progress value={stats.correctDosagePercentage} className="h-2" />
          </div>

          <div className="mt-4 bg-blue-100/70 p-3 rounded-lg">
            <h4 className="font-medium text-blue-800 flex items-center gap-1 mb-1">
              <Bell className="h-4 w-4" />
              Rappels de médicaments
            </h4>
            <p className="text-sm text-blue-700">
              Vous avez {reminderCount === 0 ? "aucun" : reminderCount} rappel{reminderCount !== 1 ? "s" : ""} actif{reminderCount !== 1 ? "s" : ""} configuré{reminderCount !== 1 ? "s" : ""}.
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Utilisez le bouton "Rappels" pour configurer vos heures de prise de médicaments et recevoir des notifications.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
